import React, { useState, useEffect } from 'react';
import { useNotification } from '../../contexts/NotificationContext';
import { useTutorial } from '../../contexts/TutorialContext';
import { getApiUrl } from '../../utils/api';
import useInlineFillHeight from '../../hooks/useInlineFillHeight';
import './NewTestCaseModal.css';

const PRIORITY_OPTIONS = ['0', '1', '2', '3', '4'];
const mapNumericPriorityToApi = (value) => {
  const n = Number(value);
  if (n <= 0) return 'highest';
  if (n === 1) return 'high';
  if (n === 2) return 'medium';
  if (n === 3) return 'low';
  return 'lowest';
};

const NewTestCaseModal = ({ isOpen, onClose, onSuccess, selectedFolder = null, projectId = '' }) => {
  const { showError, showSuccess } = useNotification();
  const tutorial = useTutorial();
  const [inlineRef, inlineHeight] = useInlineFillHeight(isOpen);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    preconditions: '',
    steps: '',
    priority: '2'
  });
  const [loading, setLoading] = useState(false);
  const [stepsMode, setStepsMode] = useState('bdd');

  const applyStepsTemplate = (mode) => {
    setStepsMode(mode);
    if (mode === 'bdd') {
      setFormData(prev => ({ ...prev, steps: 'Feature: \n  Scenario: \n    Given \n    When \n    Then \n' }));
    } else if (mode === 'passo-a-passo') {
      setFormData(prev => ({ ...prev, steps: '- Passo 1: \n- Passo 2: \n- Passo 3: \n' }));
    }
  };

  useEffect(() => {
    if (isOpen) {
      setFormData({ title: '', description: '', preconditions: '', steps: '', priority: '2' });
    }
  }, [isOpen]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim()) {
      showError('Nome do Caso de Teste é obrigatório');
      return;
    }

    // Backend exige descrição; validar no cliente para evitar 400
    if (!formData.description.trim()) {
      showError('Descrição é obrigatória');
      return;
    }

    const effectiveProjectId = projectId || selectedFolder?.project_id || '';
    if (!effectiveProjectId) {
      showError('Nenhum projeto selecionado');
      return;
    }

    if (tutorial?.isTutorialMode) {
      const nowIso = new Date().toISOString();
      let userName = 'Tutorial';
      let userId = null;
      try {
        const raw = localStorage.getItem('user');
        const parsed = raw ? JSON.parse(raw) : null;
        if (parsed?.name) userName = parsed.name;
        if (parsed?.id != null) userId = parsed.id;
      } catch (_) {}

      const created = {
        id: `tut-tc-${Date.now()}`,
        title: formData.title,
        description: formData.description,
        preconditions: formData.preconditions,
        project_id: effectiveProjectId,
        folder_id: selectedFolder?.id || null,
        created_at: nowIso,
        created_by: userId,
        created_by_name: userName,
        status: 'draft',
        priority: mapNumericPriorityToApi(formData.priority),
      };

      tutorial?.addSimulatedTestCase?.(effectiveProjectId, created);
      showSuccess('Caso de teste criado com sucesso!');
      if (typeof onSuccess === 'function') onSuccess(created);
      handleClose();
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const url = getApiUrl('testcases');
      const payload = {
        title: formData.title,
        description: formData.description,
        preconditions: formData.preconditions,
        steps: formData.steps,
        priority: mapNumericPriorityToApi(formData.priority),
        project_id: effectiveProjectId,
        folder_id: selectedFolder?.id || null
      };
      // Logs de depuração para diagnosticar erros de rede/CORS
      console.debug('[NewTestCaseModal] URL:', url);
      console.debug('[NewTestCaseModal] Token presente:', Boolean(token));
      console.debug('[NewTestCaseModal] Payload:', payload);

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        const data = await response.json();
        showSuccess('Caso de teste criado com sucesso!');
        // Backend retorna { success: true, testCase: { ... } }
        const created = data?.testCase || data?.data || null;
        onSuccess(created);
        handleClose();
      } else {
        const errorData = await response.json();
        // Exibir mensagens retornadas pelo backend incluindo detalhes úteis quando presentes
        const msg = errorData?.error || errorData?.message || 'Erro ao criar caso de teste';
        const details = errorData?.details;
        showError(details ? `${msg}: ${details}` : msg);
      }
    } catch (error) {
      console.error('Erro ao criar caso de teste:', error);
      showError(`Erro ao criar caso de teste: ${error?.message || 'Falha na requisição'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({ title: '', description: '', preconditions: '', steps: '', priority: '2' });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="modal new-test-case-modal new-test-case-inline" ref={inlineRef} style={inlineHeight ? { height: inlineHeight } : undefined}>
      <div className="modal-header">
        <h2>Novo Caso de Teste</h2>
      </div>

        <form onSubmit={handleSubmit} className="modal-form" data-tour="modal-caso-campos">
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="title">Nome do Caso de Teste *</label>
              <input
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                placeholder="Digite o nome do caso de teste"
                required
                data-tour="modal-caso-nome"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="description">Descrição</label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Descreva o objetivo do caso de teste"
                rows="3"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="preconditions">Pré Condição</label>
              <textarea
                id="preconditions"
                name="preconditions"
                value={formData.preconditions}
                onChange={handleInputChange}
                placeholder="Descreva as condições necessárias antes da execução"
                rows="2"
              />
            </div>
          </div>

          <div className="form-section">
            <div className="section-header">
              <div className="steps-mode-control">
                <label style={{ marginRight: 8 }}>Add Steps</label>
                <select value={stepsMode} onChange={(e) => applyStepsTemplate(e.target.value)}>
                  <option value="bdd">BDD</option>
                  <option value="passo-a-passo">Passo a Passo</option>
                </select>
              </div>
              <div className="priority-control-row">
                <label htmlFor="priority">Prioridade</label>
                <select
                  id="priority"
                  name="priority"
                  value={formData.priority}
                  onChange={handleInputChange}
                >
                  {PRIORITY_OPTIONS.map(option => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="step-group">
              <div className="step-header">{stepsMode === 'passo-a-passo' ? 'Passo a Passo' : 'Gherkin'}</div>
              <div className="step-content">
                <div className="form-group">
                  <textarea
                    name="steps"
                    value={formData.steps}
                    onChange={handleInputChange}
                    placeholder={stepsMode === 'bdd' ? 'Given/When/Then...' : 'Liste os passos...'}
                    style={{ minHeight: 160 }}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="modal-actions">
            <button type="button" className="btn btn-danger" onClick={handleClose}>
              Cancelar
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading} data-tour="modal-caso-salvar">
              {loading ? 'Salvando...' : 'Salvar e Fechar'}
            </button>
          </div>
        </form>
    </div>
  );
};

export default NewTestCaseModal;
